import { useQuery } from "@hooks/useQuery";
import { Pagination, TableColumnsType, Typography } from "antd";
import { get as _get } from "lodash";
import { useEffect, useState } from "react";
import { FieldValues } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { DataTable } from "~/Components";
import ConfirmationModal from "~/Components/Modal/confirmation-modal";
import { TableDrawer } from "~/Components/TableDrawer/TableDrawer";
import { useLocalStorage } from "~/Hooks/useLocalStorage";
import { useNotification } from "~/Hooks/useNotification";
import { passengerCMSApi, post } from "~/Services";
import { passengerHost } from "~/Utils";
import ActionDropdown, { TypeData } from "~/Utils/ActionDropdown";
import { decodeHTML } from "~/Utils/CommonUtils";
import { drilldownData, ModuleConfig } from "~/Utils/drilldownData";
import {
  passengerAirportColumn,
  renderActionsColumn,
  renderDateColumn,
  renderImageColumn,
  renderPublishedColumnStatus,
} from "~/Utils/TableRenderFunction";
import { useSubCategoriesDocumentDetails } from "./Hooks/Cards.hook";
const SpotlightCards = () => {
  const query = useQuery();
  const navigate = useNavigate();
  const { openToast } = useNotification();
  const [currentAirport] = useLocalStorage("currentAirport", null);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [selectedRowData, setSelectedRowData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState<any>([]);
  const [sortedData, setSortedData] = useState<any>([]);
  const [searchValue, setSearchValue] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [sortOrder, setSortOrder] = useState<string | undefined>();
  const [orderByName, setOrderByName] = useState("id");
  const [selectedPublished, setSelectedPublished] = useState<any>("");

  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [deleteConfirmLoading, setDeleteConfirmLoading] = useState(false);
  const [id, setId] = useState<string | undefined>(selectedRowData?.id);

  const publishedData: TypeData[] = [
    { label: "Yes", value: "True" },
    { label: "No", value: "False" },
  ];

  const { data: drilldown, refetch: refetchDocument } = useSubCategoriesDocumentDetails(id || "");

  useEffect(() => {
    if (id) {
      refetchDocument();
    }
  }, [id]);
  useEffect(() => {
    setId(selectedRowData?.id);
  }, [selectedRowData]);
  const getRecords = () => {
    setIsLoading(true);
    passengerCMSApi
      .get(`/api/get-spotlight-section-one`)
      .then((res: any) => {
        if (res?.data) {
          let mappedData = res.data;
          if (currentAirport) {
            mappedData = mappedData.filter((item: any) => {
              if (!item.airports) return false;

              const airportList = Array.isArray(item.airports)
                ? item.airports.map((a: any) => a.trim().toLowerCase())
                : item.airports.split(",").map((a: string) => a.trim().toLowerCase());

              return airportList.includes(currentAirport.toLowerCase());
            });
          }
          setData(mappedData);
          setTotalRecords(mappedData.length);
        } else {
          setData([]);
        }
        setIsLoading(false);
      })
      .catch(() => {
        setData([]);
        setIsLoading(false);
      });
  };

  useEffect(() => {
    getRecords();
  }, []);

  // 🟩 Added filtering & sorting logic same as PageContent
  useEffect(() => {
    const safeData = Array.isArray(data) ? data : [];
    let filteredData = [...safeData];

    if (searchValue) {
      const searchLower = searchValue.toLowerCase();
      filteredData = filteredData.filter((item: any) =>
        String(item.title || "")
          .toLowerCase()
          .includes(searchLower)
      );
    }

    if (selectedPublished !== "") {
      const isPublished = String(selectedPublished).toLowerCase() === "true";
      filteredData = filteredData.filter((item: any) => {
        const itemPublished = String(item.published).toLowerCase() === "true";
        return itemPublished === isPublished;
      });
    }

    setSortedData(filteredData);
    setTotalRecords(filteredData.length);
    setCurrentPage(0);
  }, [data, searchValue, selectedPublished]);

  const handleTableSort = (column: any, sortOrder: string) => {
    let order = "";
    if (sortOrder === "asc" && orderByName === column.dataIndex) {
      setSortOrder("desc");
      order = "desc";
    } else {
      setSortOrder("asc");
      order = "asc";
    }

    setOrderByName(column.dataIndex);
    const tempData = [...sortedData];
    setSortedData(tempData.sort((a: any, b: any) => handleSort(a, b, order, column.dataIndex)));
    setCurrentPage(0);
  };

  const columns: TableColumnsType<any> = [
    {
      title: "Sequence",
      dataIndex: "sequence",
      key: "sequence",
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "sequence"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "sequence"
            ? "descend"
            : undefined,
    },
    {
      title: "Image",
      dataIndex: "banner",
      key: "banner",
      render: (banner: string) => renderImageColumn(banner),
    },
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
      render: (text) => decodeHTML(text),
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "title"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "title"
            ? "descend"
            : undefined,
    },
    {
      title: "Airports",
      dataIndex: "airports",
      key: "airports",
      render: (text) => passengerAirportColumn(text),
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "airports"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "airports"
            ? "descend"
            : undefined,
    },
    // {
    //   title: "Link",
    //   dataIndex: "link",
    //   key: "link",
    //   render: (url: string) =>
    //     url ? (
    //       <a
    //         href={url.startsWith("http") ? url : `https://${url}`}
    //         target="_blank"
    //         rel="noreferrer"
    //       >
    //         {url}
    //       </a>
    //     ) : (
    //       "-"
    //     ),
    // },
    {
      title: "Published",
      dataIndex: "published",
      key: "published",
      render: (text) => renderPublishedColumnStatus(text),
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "published"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "published"
            ? "descend"
            : undefined,
    },
    {
      title: "Created At",
      dataIndex: "created",
      key: "created",
      render: renderDateColumn,
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "created"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "created"
            ? "descend"
            : undefined,
    },
    {
      title: "Updated At",
      dataIndex: "changed",
      key: "changed",
      render: (value: any, record: any) => {
        if (value && record.created && value === record.created) {
          return "-";
        }
        return renderDateColumn(value);
      },
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "changed"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "changed"
            ? "descend"
            : undefined,
    },
    {
      title: "Actions",
      key: "actions",
      render: (_: any, record: any) =>
        renderActionsColumn(
          record,
          setSelectedRowData,
          setDrawerVisible,
          (e: any) => {
            e.preventDefault();
            navigate(`/dedicated/passenger/homepage/spotlight/card/add?editId=${record.id}`);
          },
          undefined,
          setDeleteModalVisible,
          "users"
        ),
    },
  ];
  const onSubmit = async (
    data: FieldValues,
    apiUrl: string,
    action: string,
    method: string,
    status: "DRAFT" | "PENDING",
    reviewNotes?: string
  ) => {
    if (apiUrl === "") return;

    const payload = {
      action,
      method,
      module: "Home Page",
      subModule: "cards",
      type: "DEDICATED",
      application: "PASSENGER_ECMS",
      reviewNotes,
      appList: [{ appName: "PASSENGER_ECMS", apiUrl }],
      payload: data,
      airportName: currentAirport,
      status,
    };

    post("/management/publish", payload)
      .then((response) => {
        openToast({
          content: "Data Sent for Review",
          type: "success",
        });
        navigate("/dedicated/status-information?activeTab=pending");
      })
      .catch((err: any) => {
        openToast({
          content: "Failed to send data",
          type: "error",
        });
      });
  };

  async function sendDeleteBrandRequest(reviewNotes: string) {
    try {
      await onSubmit(
        selectedRowData,
        `${passengerHost}/api/spotlight/section-one/${selectedRowData?.id}`,
        "DELETE",
        "DELETE",
        "PENDING",
        reviewNotes
      );
    } catch (err) {}
  }

  return (
    <>
      <DataTable
        tabOffset={60}
        columns={columns.map((col) => ({
          ...col,
          onHeaderCell: (column: any) => (col.sorter ? { onClick: () => handleTableSort(col, sortOrder) } : {}),
        }))}
        multiSelect={false}
        className="table-clickable"
        tableData={currentPageData(sortedData, currentPage, pageSize)}
        isLoading={isLoading}
        exportFileName="SPOTLIGHT"
        isPagination
        recordsPerPage={pageSize}
        hideExport={true}
        isServerSearchApi={false}
        searchPlaceholderText={"Search Title"}
        onSearchChange={(text) => {
          setSearchValue(text);
          setCurrentPage(0);
        }}
        onClick={(record) => {
          setSelectedRowData(record);
        }}
        additionalCTA={
          <div className="flex items-center space-x-2">
            <ActionDropdown
              value={selectedPublished}
              options={publishedData}
              onChange={(val) => setSelectedPublished(val)}
              placeholder="Select Published"
              className="w-48"
            />
          </div>
        }
      />
      <div className="pagination flex justify-end mt-4">
        <Pagination
          defaultCurrent={1}
          current={currentPage + 1}
          total={totalRecords}
          pageSize={pageSize}
          onChange={(curPage, newPageSize) => {
            setCurrentPage(curPage - 1);
            if (newPageSize !== pageSize) {
              setPageSize(newPageSize);
              setCurrentPage(0);
            }
          }}
          showSizeChanger
          pageSizeOptions={["10", "20", "50", "100"]}
        />
      </div>
      <TableDrawer
        title={
          <Typography.Title
            level={4}
            className={"!mb-0"}
          >
            Detailed View
          </Typography.Title>
        }
        visible={drawerVisible}
        onClose={() => setDrawerVisible(false)}
        fields={drilldownData[_get(drilldown, "subModule", "cards") as ModuleConfig]}
        data={drilldown}
      />

      <ConfirmationModal
        type="delete"
        open={deleteModalVisible}
        onClose={() => setDeleteModalVisible(false)}
        onSubmit={sendDeleteBrandRequest}
        defaultReviewNotes="Kindly review this Homepage"
      />
    </>
  );
};
export default SpotlightCards;

function currentPageData(data: any[], currentPage: number, pageSize: number) {
  return data.slice(currentPage * pageSize, (currentPage + 1) * pageSize);
}

export function handleSort(a: any, b: any, order: string, sortBy: string) {
  if (["title", "sequence"].includes(sortBy)) {
    const titleA = a[sortBy]?.toString().toLowerCase() || "";
    const titleB = b[sortBy]?.toString().toLowerCase() || "";
    if (order === "desc") return titleA < titleB ? 1 : -1;
    if (order === "asc") return titleA > titleB ? 1 : -1;
    return 0;
  }

  if (order === "desc") return a[sortBy] < b[sortBy] ? 1 : -1;
  if (order === "asc") return a[sortBy] > b[sortBy] ? 1 : -1;
  return 0;
}
